import {
  DatabaseAuto,
  IdataArrayResponse,
  IdeleteOneResponse,
  IfilterProps,
  InotifApiKey
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { NotificationClient } from '../notif-client';

/**
 * Represents a notification API key with its associated configuration and usage limits.
 * This class provides methods to manage notification API keys and their usage statistics.
 *
 * @class NotificationApiKey
 * @extends DatabaseAuto
 * @example
 * // Get all API keys with pagination
 * const { count, dataArr } = await NotificationApiKey.getAll(0, 10);
 *
 * // Create a new API key
 * const newKey = await NotificationApiKey.add({
 *   enabled: true,
 *   owner: 'user123',
 *   maxEmails: 1000,
 *   maxSms: 500
 * });
 */
export class NotificationApiKey extends DatabaseAuto {
  /** Base URL for notification API key endpoints */
  private static readonly baseUrl = '/notification-api-key';

  /** The API key string used for authentication */
  apiKey: InotifApiKey['apiKey'];

  /** Whether the API key is currently enabled */
  enabled: InotifApiKey['enabled'];

  /** Optional expiration date for the API key */
  expiresAt?: InotifApiKey['expiresAt'];

  /** The owner/creator of the API key */
  owner: InotifApiKey['owner'];

  /** Maximum number of allowed emails (optional) */
  maxEmails?: InotifApiKey['maxEmails'];

  /** Maximum number of allowed SMS messages (optional) */
  maxSms?: InotifApiKey['maxSms'];

  /** Number of emails sent using this key (optional) */
  usedEmails?: InotifApiKey['usedEmails'];

  /** Number of SMS messages sent using this key (optional) */
  usedSms?: InotifApiKey['usedSms'];

  /** Last Used timestamp */
  lastUsedAt?: InotifApiKey['lastUsedAt'];

  /**
   * Creates a new NotificationApiKey instance
   * @param notifApiKey - The notification API key data
   */
  constructor(notifApiKey: InotifApiKey) {
    super(notifApiKey);
    this.apiKey = notifApiKey.apiKey;
    this.enabled = notifApiKey.enabled;
    this.expiresAt = notifApiKey.expiresAt;
    this.owner = notifApiKey.owner;
    this.maxEmails = notifApiKey.maxEmails;
    this.maxSms = notifApiKey.maxSms;
    this.usedEmails = notifApiKey.usedEmails;
    this.usedSms = notifApiKey.usedSms;
    this.lastUsedAt = notifApiKey.lastUsedAt;
  }

  /**
   * Retrieves a paginated list of notification API keys
   * @param {number} offset - The number of items to skip before starting to collect the result set
   * @param {number} limit - The maximum number of items to return
   * @returns {Promise<{count: number, dataArr: NotificationApiKey[]}>}
   * An object containing the total count and array of API keys
   * @throws {Error} If the API request fails
   * @example
   * // Get first 10 API keys
   * const { count, dataArr } = await NotificationApiKey.getAll(0, 10);
   */
  static async getAll(offset: number, limit: number) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = NotificationClient.ehttp
      .makeGet<IdataArrayResponse<InotifApiKey>>(`${NotificationApiKey.baseUrl}?${query}`);
    const response = await lastValueFrom(observer$);

    return {
      count: response.count,
      dataArr: response.data.map(val => new NotificationApiKey(val))
    };
  }

  /**
   * Retrieves notification API keys based on filter criteria
   * @param {IfilterProps} filter - The filter criteria to apply
   * @returns {Promise<{count: number, dataArr: NotificationApiKey[]}>} Filtered API keys with count
   * @throws {Error} If the API request fails
   * @example
   * // Get all enabled API keys
   * const { dataArr } = await NotificationApiKey.filterAll({
   *   conditions: { enabled: true }
   * });
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = NotificationClient.ehttp
      .makePost<IdataArrayResponse<InotifApiKey>>(
        `${NotificationApiKey.baseUrl}/filter`,
        { filter }
      );
    const response = await lastValueFrom(observer$);

    return {
      count: response.count,
      dataArr: response.data.map(val => new NotificationApiKey(val))
    };
  }

  /**
   * Retrieves a single notification API key by ID
   * @param {string} resourceId - The ID of the API key to retrieve
   * @returns {Promise<NotificationApiKey>} The requested API key
   * @throws {Error} If the API key is not found or request fails
   * @example
   * const apiKey = await NotificationApiKey.getOne('123abc');
   */
  static async getOne(resourceId: string) {
    const observer$ = NotificationClient.ehttp
      .makeGet<InotifApiKey>(`${NotificationApiKey.baseUrl}/${resourceId}`);
    const response = await lastValueFrom(observer$);

    return new NotificationApiKey(response);
  }

  /**
   * Creates a new notification API key
   * @param {Omit<InotifApiKey, '_id' | 'createdAt' | 'updatedAt'
   * | 'apiKey' | 'usedEmails' | 'usedSms'>} payload - The API key configuration
   * @returns {Promise<NotificationApiKey>} The newly created API key
   * @throws {Error} If the API request fails or validation fails
   * @example
   * const newKey = await NotificationApiKey.add({
   *   enabled: true,
   *   owner: 'user123',
   *   maxEmails: 1000,
   *   maxSms: 500
   * });
   */
  static async add(payload: Omit<
    InotifApiKey, '_id' | 'createdAt' | 'updatedAt' | 'apiKey' | 'usedEmails' | 'usedSms'>) {
    const observer$ = NotificationClient.ehttp
      .makePost<InotifApiKey>(`${NotificationApiKey.baseUrl}`, payload);

    return new NotificationApiKey(await lastValueFrom(observer$));
  }

  /**
   * Updates the current API key's configuration
   * @param {Omit<Partial<InotifApiKey>, '_id' | 'createdAt' |
   * 'updatedAt' | 'apiKey' | 'usedEmails' | 'usedSms'>} payload - The fields to update
   * @returns {Promise<NotificationApiKey>} The updated API key
   * @throws {Error} If the update fails or API key doesn't exist
   * @example
   * // Disable an API key
   * await apiKeyInstance.update({ enabled: false });
   */
  async update(payload: Omit<
    Partial<InotifApiKey>, '_id' | 'createdAt' | 'updatedAt' | 'apiKey' | 'usedEmails' | 'usedSms'>) {
    const observer$ = NotificationClient.ehttp
      .makePut<InotifApiKey>(`${NotificationApiKey.baseUrl}/${this._id}`, payload);

    return new NotificationApiKey(await lastValueFrom(observer$));
  }

  /**
   * Deletes the current API key
   * @returns {Promise<IdeleteOneResponse | null>} The result of the delete operation
   * @throws {Error} If the deletion fails
   * @example
   * const result = await apiKeyInstance.remove();
   * if (result?.success) {
   *   console.log('API key deleted successfully');
   * }
   */
  async remove(): Promise<IdeleteOneResponse | null> {
    const observer$ = NotificationClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${NotificationApiKey.baseUrl}/${this._id}`);
    const response = await lastValueFrom(observer$);

    return response;
  }

  /**
   * Checks if the API key has expired
   * @returns {boolean} True if the API key has expired, false otherwise
   */
  isExpired() {
    return this.expiresAt ? new Date(this.expiresAt) < new Date() : false;
  }

  /**
   * Checks if the API key can send an email
   * @returns {boolean} True if the API key can send an email, false otherwise
   */
  canSendEmail() {
    if (!this.enabled || this.isExpired()) {
      return false
      ;}

    return !this.maxEmails || (this.usedEmails || 0) < this.maxEmails;
  }
}
